import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import articleService from '../../services/articleService';

const WorkflowContainer = styled.div`
  padding: 2rem 0;
`;

const WorkflowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const WorkflowTitle = styled.h1`
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ArticlePreview = styled.div`
  background-color: white;
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const PreviewTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 0.5rem;
`;

const PreviewSubtitle = styled.h3`
  font-family: ${props => props.theme.fonts.body};
  font-weight: normal;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-style: italic;
`;

const PreviewMeta = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.text};
  opacity: 0.8;
  
  span:not(:last-child):after {
    content: '•';
    margin-left: 1rem;
  }
`;

const PreviewImage = styled.div`
  width: 100%;
  height: 300px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  margin-bottom: 1.5rem;
  border-radius: 4px;
`;

const PreviewContent = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding-right: 1rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid ${props => props.theme.colors.divider};
  padding-bottom: 1.5rem;
`;

const WorkflowStatus = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: 8px;
  padding: 2rem;
`;

const StatusTitle = styled.h3`
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.primary};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 2rem;
  background-color: ${props => {
    switch(props.status) {
      case 'draft': return '#E3F2FD';
      case 'review': return '#FFF9C4';
      case 'approved': return '#E8F5E9';
      case 'published': return '#E0F2F1';
      default: return '#EEEEEE';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'draft': return '#1565C0';
      case 'review': return '#F57F17';
      case 'approved': return '#2E7D32';
      case 'published': return '#00695C';
      default: return '#616161';
    }
  }};
`;

const StatusTimeline = styled.div`
  margin-bottom: 2rem;
`;

const TimelineItem = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:not(:last-child):before {
    content: '';
    position: absolute;
    left: 12px;
    top: 24px;
    bottom: -12px;
    width: 2px;
    background-color: ${props => props.theme.colors.divider};
  }
`;

const TimelineDot = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.divider};
  margin-right: 1rem;
`;

const TimelineContent = styled.div`
  flex: 1;
`;

const TimelineTitle = styled.h4`
  margin-bottom: 0.25rem;
  color: ${props => props.active ? props.theme.colors.text : props.theme.colors.text + '99'};
`;

const TimelineDate = styled.p`
  font-size: 0.9rem;
  color: ${props => props.active ? props.theme.colors.text + 'CC' : props.theme.colors.text + '66'};
`;

const FeedbackForm = styled.div`
  margin-top: 2rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: 4px;
  font-family: ${props => props.theme.fonts.body};
  min-height: 100px;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: transparent;
  color: ${props => props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.divider};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.background};
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  margin-top: 0.5rem;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.colors.success};
  margin-top: 0.5rem;
  font-size: 0.9rem;
`;

const WorkflowManager = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [article, setArticle] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const article = await articleService.getArticle(id);
        
        if (article) {
          setArticle(article);
        } else {
          setError('Article not found');
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        setError('Error loading article');
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticle();
  }, [id]);
  
  const handleStatusUpdate = async (newStatus) => {
    try {
      setUpdating(true);
      setError('');
      setSuccess('');
      
      const updates = {
        status: newStatus,
        updated_at: new Date().toISOString()
      };
      
      if (feedback.trim()) {
        updates.feedback = {
          text: feedback,
          by: currentUser?.displayName || 'Unknown User',
          at: new Date().toISOString()
        };
      }
      
      // Add publishedAt timestamp if status is published
      if (newStatus === 'published') {
        updates.published_at = new Date().toISOString();
      }
      
      await articleService.updateArticle(id, updates);
      
      // Update local state
      setArticle(prev => ({
        ...prev,
        status: newStatus,
        updated_at: new Date().toISOString(),
        ...(feedback.trim() ? {
          feedback: {
            text: feedback,
            by: currentUser?.displayName || 'Unknown User',
            at: new Date().toISOString()
          }
        } : {}),
        ...(newStatus === 'published' ? { published_at: new Date().toISOString() } : {})
      }));
      
      setSuccess(`Article status updated to ${newStatus}`);
      setFeedback('');
    } catch (error) {
      console.error('Error updating article status:', error);
      setError('Failed to update article status');
    } finally {
      setUpdating(false);
    }
  };
  
  if (loading) {
    return (
      <div className="container">
        <WorkflowContainer>Loading article workflow...</WorkflowContainer>
      </div>
    );
  }
  
  if (!article) {
    return (
      <div className="container">
        <WorkflowContainer>
          <WorkflowHeader>
            <WorkflowTitle>Article Workflow</WorkflowTitle>
            <BackButton onClick={() => navigate('/admin')}>
              Back to Dashboard
            </BackButton>
          </WorkflowHeader>
          <ErrorMessage>{error || 'Article not found'}</ErrorMessage>
        </WorkflowContainer>
      </div>
    );
  }
  
  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'Pending';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Determine which actions are available based on current status
  const getAvailableActions = () => {
    switch(article.status) {
      case 'draft':
        return ['review'];
      case 'review':
        return ['approved', 'draft'];
      case 'approved':
        return ['published', 'review'];
      case 'published':
        return [];
      default:
        return [];
    }
  };
  
  const availableActions = getAvailableActions();
  
  return (
    <div className="container">
      <WorkflowContainer>
        <WorkflowHeader>
          <WorkflowTitle>Article Workflow</WorkflowTitle>
          <BackButton onClick={() => navigate('/admin')}>
            Back to Dashboard
          </BackButton>
        </WorkflowHeader>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        <ArticlePreview>
          <PreviewTitle>{article.title}</PreviewTitle>
          {article.subtitle && <PreviewSubtitle>{article.subtitle}</PreviewSubtitle>}
          
          <PreviewMeta>
            <span>By {article.author}</span>
            <span>Section: {article.section}</span>
            <span>Created: {formatDate(article.created_at)}</span>
            <span>Updated: {formatDate(article.updated_at)}</span>
          </PreviewMeta>
          
          {article.image_url && <PreviewImage src={article.image_url} />}
          
          <PreviewContent dangerouslySetInnerHTML={{ __html: article.content }} />
        </ArticlePreview>
        
        <WorkflowStatus>
          <StatusTitle>Workflow Status</StatusTitle>
          <StatusBadge status={article.status}>
            {article.status}
          </StatusBadge>
          
          <StatusTimeline>
            <TimelineItem>
              <TimelineDot active={true} />
              <TimelineContent>
                <TimelineTitle active={true}>Draft Created</TimelineTitle>
                <TimelineDate active={true}>{formatDate(article.created_at)}</TimelineDate>
              </TimelineContent>
            </TimelineItem>
            
            <TimelineItem>
              <TimelineDot active={article.status !== 'draft'} />
              <TimelineContent>
                <TimelineTitle active={article.status !== 'draft'}>Submitted for Review</TimelineTitle>
                <TimelineDate active={article.status !== 'draft'}>
                  {article.status !== 'draft' ? formatDate(article.updated_at) : 'Pending'}
                </TimelineDate>
              </TimelineContent>
            </TimelineItem>
            
            <TimelineItem>
              <TimelineDot active={article.status === 'approved' || article.status === 'published'} />
              <TimelineContent>
                <TimelineTitle active={article.status === 'approved' || article.status === 'published'}>Approved</TimelineTitle>
                <TimelineDate active={article.status === 'approved' || article.status === 'published'}>
                  {article.status === 'approved' || article.status === 'published' ? formatDate(article.updated_at) : 'Pending'}
                </TimelineDate>
              </TimelineContent>
            </TimelineItem>
            
            <TimelineItem>
              <TimelineDot active={article.status === 'published'} />
              <TimelineContent>
                <TimelineTitle active={article.status === 'published'}>Published</TimelineTitle>
                <TimelineDate active={article.status === 'published'}>
                  {article.status === 'published' ? formatDate(article.published_at) : 'Pending'}
                </TimelineDate>
              </TimelineContent>
            </TimelineItem>
          </StatusTimeline>
          
          {availableActions.length > 0 && (
            <FeedbackForm>
              <TextArea 
                placeholder="Add feedback or notes about this article..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
              />
              
              <ButtonGroup>
                {availableActions.includes('draft') && (
                  <SecondaryButton 
                    onClick={() => handleStatusUpdate('draft')} 
                    disabled={updating}
                  >
                    Return to Draft
                  </SecondaryButton>
                )}
                
                {availableActions.includes('review') && (
                  <PrimaryButton 
                    onClick={() => handleStatusUpdate('review')} 
                    disabled={updating}
                  >
                    Submit for Review
                  </PrimaryButton>
                )}
                
                {availableActions.includes('approved') && (
                  <PrimaryButton 
                    onClick={() => handleStatusUpdate('approved')} 
                    disabled={updating}
                  >
                    Approve Article
                  </PrimaryButton>
                )}
                
                {availableActions.includes('published') && (
                  <PrimaryButton 
                    onClick={() => handleStatusUpdate('published')} 
                    disabled={updating}
                  >
                    Publish Article
                  </PrimaryButton>
                )}
              </ButtonGroup>
            </FeedbackForm>
          )}
        </WorkflowStatus>
      </WorkflowContainer>
    </div>
  );
};

export default WorkflowManager;